import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { usePointCloudContext } from 'features/pointClouds'
import { useObservationImages } from 'pages/pointClouds/utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { GeckoSiteObservation, TicketStatus } from 'types'

import { useObservationHandlers } from './useObservationHandlers'

interface UseObservationDialogueProps {
  observation?: GeckoSiteObservation
}

export const useObservationDialogue = ({
  observation,
}: UseObservationDialogueProps) => {
  const { rolodexDeleteFile } = useRolodexEdit()

  const initialImages = useObservationImages(observation?.id)

  const [currentImages, setCurrentImages] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    setCurrentImages([...initialImages])
  }, [observation?.id, initialImages])

  const { handleDeleteObservation: onDelete, handleSaveObservation: onSave } =
    useObservationHandlers()

  const { setObservationPosition, setEditingObservation } =
    usePointCloudContext()

  const handleCloseDialogue = useCallback(() => {
    setObservationPosition(null)
    setEditingObservation(null)
  }, [setObservationPosition, setEditingObservation])

  const handleCancel = useCallback(() => {
    handleCloseDialogue()
  }, [handleCloseDialogue])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [handleCancel])

  const handleSave = async (
    formData: Partial<GeckoSiteObservation['data']> & {
      x?: string
      y?: string
      z?: string
    }
  ) => {
    if (formData.description?.trim()) {
      setIsSaving(true)

      const x = parseFloat(formData.x || '')
      const y = parseFloat(formData.y || '')
      const z = parseFloat(formData.z || '')
      const hasValidCoords = !isNaN(x) && !isNaN(y) && !isNaN(z)

      const pointCloudCoordinates = hasValidCoords
        ? { coordinate: [x, y, z] as [number, number, number] }
        : formData.pointCloudCoordinates || undefined

      const observationToSave: Partial<GeckoSiteObservation> = {
        id: observation?.id,
        data: {
          ...formData,
          id: observation?.id || undefined,
          kind: 'point',
          status: formData.status || TicketStatus.NEW,
          date_found:
            String(formData.dateFound || '') === ''
              ? undefined
              : String(formData.dateFound),
          notification_number:
            String(formData.notificationNumber || '') === ''
              ? undefined
              : String(formData.notificationNumber),
          wo_number:
            String(formData.woNumber || '') === ''
              ? undefined
              : String(formData.woNumber),
          pointCloudCoordinates: pointCloudCoordinates,
        },
      }

      try {
        await onSave(
          observationToSave,
          currentImages.length > 0 ? currentImages : undefined
        )
        setIsSaving(false)
        handleCloseDialogue()
      } catch (error) {
        console.error('Save failed:', error)
        setIsSaving(false)
      }
    }
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      const newImagesPromises = Array.from(files).map((file) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (event) => {
            if (event.target?.result) {
              resolve(event.target.result as string)
            } else {
              reject(new Error('Failed to read file'))
            }
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
      })

      Promise.all(newImagesPromises)
        .then((newImageUrls) => {
          setCurrentImages((prev) => [...prev, ...newImageUrls])
        })
        .catch((error) => {
          console.error('Error reading files:', error)
        })
    }
  }

  const handleRemoveImage = async (index: number) => {
    const imageToRemove = currentImages[index]
    if (!imageToRemove) return

    setCurrentImages((prev) => prev.filter((_, i) => i !== index))

    if (observation?.id) {
      const fileName =
        imageToRemove.split('/').pop()?.split('?')[0] ||
        `image_${index + 1}.jpg`

      await rolodexDeleteFile(observation?.id, fileName)
    }
  }

  const handleDelete = async () => {
    if (onDelete && observation?.id) {
      setIsDeleting(true)
      try {
        await onDelete(observation?.id)
        setIsDeleting(false)
        handleCloseDialogue()
      } catch (error) {
        console.error('Delete failed:', error)
        setIsDeleting(false)
      }
    }
  }

  return {
    currentImages,
    fileInputRef,
    isSaving,
    isDeleting,
    handleCancel,
    handleSave,
    handleImageUpload,
    handleRemoveImage,
    handleDelete,
    handleCloseDialogue,
  }
}
